import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  ASSESSMENT_OF_SYMPTOMS,
  useGenerateCareDrafts,
  type CarePresentation,
  type CareUpdateFields,
} from "@/lib/api/care";
import { useDevOverridesStore } from "@/lib/hooks/admin/use-dev-overrides";
import { REDACTED_LABEL } from "@/lib/redacted-display";
import { PostHogEvents, trackEvent } from "@/lib/posthog-events";
import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { Copy, Loader2, Sparkles } from "lucide-react";
import { useRef, useState, type ReactNode } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { FieldLabel } from "./care-primitives";

const TEXTAREA_CLASS =
  "h-[120px] resize-none rounded-xl border-[#8a8275] bg-white/5 px-6 py-4 font-body text-base leading-6 text-text-primary placeholder:text-[#a0998d]";

export const careUpdateFormSchema = z.object({
  assessment_of_symptoms: z.enum(ASSESSMENT_OF_SYMPTOMS),
  clinical_summary: z.string().trim().min(1),
  diagnostic_support_statement: z.string().trim().min(1),
});

export const INCOMPLETE_MESSAGE =
  "All three fields need to be completed before you can submit this update.";

export type CareUpdateFormValues = Partial<CareUpdateFields>;

interface CareUpdateFormProps {
  menteeId: string;
  presentation?: CarePresentation;
  defaultValues?: CareUpdateFormValues;
  onSubmit: (values: CareUpdateFields) => void;
  pending: boolean;
  pendingAction?: "submit" | "secondary";
  submitLabel?: string;
  secondaryAction?: {
    label: string;
    onClick: (values: CareUpdateFormValues) => void;
  };
  title?: string;
}

export function CareUpdateForm({
  menteeId,
  presentation,
  defaultValues,
  onSubmit,
  pending,
  pendingAction = "submit",
  submitLabel = "Submit",
  secondaryAction,
  title,
}: CareUpdateFormProps) {
  const hidePhi = useDevOverridesStore((state) => state.hidePhiPii) === true;
  const form = useForm<CareUpdateFields>({
    resolver: zodResolver(careUpdateFormSchema),
    defaultValues,
    reValidateMode: "onSubmit",
  });
  const { errors } = form.formState;
  const incomplete = Object.keys(errors).length > 0;
  const drafts = useGenerateCareDrafts(menteeId);
  // drafts are written for one presentation; a new code needs a fresh generation
  const draftedFor = useRef<string | null>(null);
  const presentationCode = presentation?.code ?? null;
  const draft =
    draftedFor.current === presentationCode ? drafts.data : undefined;
  const openAiNotes =
    (field: "clinical_summary" | "diagnostic_support_statement") => () => {
      trackEvent(PostHogEvents.CARE_PAGE_REVIEW_VIEW_AI_NOTES_CLICKED, {
        mentee_id: menteeId,
        field,
      });
      const code = presentation?.code ?? null;
      const stale = drafts.data && draftedFor.current !== code;
      if ((!drafts.data || stale) && !drafts.isPending) {
        draftedFor.current = code;
        drafts.mutate({ presentation });
      }
    };

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      noValidate
      className="flex flex-col gap-8"
    >
      {title ? (
        <h3 className="font-heading text-base font-bold leading-6 text-text-primary">
          {title}
        </h3>
      ) : null}

      <FormRow label="Assessment of symptoms">
        <Controller
          control={form.control}
          name="assessment_of_symptoms"
          render={({ field }) => (
            <Select value={field.value ?? ""} onValueChange={field.onChange}>
              <SelectTrigger
                className={cn(
                  "h-14 rounded-xl border-[#8a8275] bg-white py-4 pl-6 pr-4 font-body text-base leading-6 text-text-primary data-[placeholder]:text-[#a0998d] [&>svg]:size-5 [&>svg]:opacity-100",
                  errors.assessment_of_symptoms && "border-states-error",
                )}
              >
                <SelectValue placeholder="Select an assessment" />
              </SelectTrigger>
              <SelectContent>
                {ASSESSMENT_OF_SYMPTOMS.map((option) => (
                  <SelectItem
                    key={option}
                    value={option}
                    className="capitalize"
                  >
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      </FormRow>

      <FormRow
        label="Clinical Summary"
        hint="Description of current clinical presentation, including any new/recent symptom progression or regression."
      >
        <div className="flex flex-col gap-1">
          <Textarea
            {...form.register("clinical_summary")}
            placeholder="Placeholder text"
            readOnly={hidePhi}
            aria-hidden={hidePhi}
            tabIndex={hidePhi ? -1 : undefined}
            className={cn(
              hidePhi ? "hidden" : TEXTAREA_CLASS,
              errors.clinical_summary && "border-states-error",
            )}
          />
          {hidePhi ? <RedactedNote /> : null}
          <AiNotes
            key={`${presentationCode ?? ""}-clinical`}
            text={hidePhi ? REDACTED_LABEL : (draft?.clinical_summary ?? null)}
            pending={hidePhi ? false : drafts.isPending}
            onOpen={hidePhi ? () => {} : openAiNotes("clinical_summary")}
          />
        </div>
      </FormRow>

      <FormRow
        label="Diagnostic support statement"
        hint="Symptom evidence supporting the diagnosis (or, if changed, evidence supporting the updated diagnosis)."
      >
        <div className="flex flex-col gap-1">
          <Textarea
            {...form.register("diagnostic_support_statement")}
            placeholder="Placeholder text"
            readOnly={hidePhi}
            aria-hidden={hidePhi}
            tabIndex={hidePhi ? -1 : undefined}
            className={cn(
              hidePhi ? "hidden" : TEXTAREA_CLASS,
              errors.diagnostic_support_statement && "border-states-error",
            )}
          />
          {hidePhi ? <RedactedNote /> : null}
          <AiNotes
            key={`${presentationCode ?? ""}-diagnostic`}
            text={
              hidePhi
                ? REDACTED_LABEL
                : (draft?.diagnostic_support_statement ?? null)
            }
            pending={hidePhi ? false : drafts.isPending}
            onOpen={
              hidePhi ? () => {} : openAiNotes("diagnostic_support_statement")
            }
          />
        </div>
      </FormRow>

      <div className="flex flex-col items-end gap-2">
        <div className="flex items-center gap-2">
          {secondaryAction ? (
            <button
              type="button"
              disabled={pending}
              onClick={() => secondaryAction.onClick(form.getValues())}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-full border border-[#dfddd8] bg-white px-6 font-heading text-sm font-bold text-text-primary disabled:opacity-50"
            >
              {pending && pendingAction === "secondary" ? (
                <Loader2 className="size-4 animate-spin" />
              ) : null}
              {secondaryAction.label}
            </button>
          ) : null}
          <button
            type="submit"
            disabled={pending}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-full bg-black px-6 font-heading text-sm font-bold text-white disabled:opacity-50"
          >
            {pending && pendingAction === "submit" ? (
              <Loader2 className="size-4 animate-spin" />
            ) : null}
            {submitLabel}
          </button>
        </div>
        {incomplete ? (
          <p className="font-body text-xs text-states-error">
            {INCOMPLETE_MESSAGE}
          </p>
        ) : null}
      </div>
    </form>
  );
}

function RedactedNote() {
  return <p className={TEXTAREA_CLASS}>{REDACTED_LABEL}</p>;
}

function FormRow({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-0.5">
        <FieldLabel className="text-sm font-bold">{label}</FieldLabel>
        {hint ? (
          <FieldLabel className="text-[#b5b0a6]">{hint}</FieldLabel>
        ) : null}
      </div>
      {children}
    </div>
  );
}

function AiNotes({
  text,
  pending,
  onOpen,
}: {
  text: string | null;
  pending: boolean;
  onOpen: () => void;
}) {
  const [open, setOpen] = useState(false);
  const toggle = () => {
    if (!open) onOpen();
    setOpen((value) => !value);
  };

  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        onClick={toggle}
        className="inline-flex h-6 items-center gap-1.5 self-start rounded-lg py-1 pl-1 pr-2 font-body text-sm font-bold leading-4 text-[#6e6b67]"
      >
        <Sparkles className="size-[18px]" aria-hidden />
        {open ? "Hide AI notes" : "View AI notes"}
      </button>
      {open ? (
        <div className="rounded-2xl border border-[#dfddd8] bg-surfaces-z3 p-4">
          <div className="flex items-center justify-between">
            <p className="inline-flex items-center gap-2 font-heading text-sm font-bold text-text-secondary">
              <Sparkles className="size-4" aria-hidden />
              AI Notes
            </p>
            {text ? (
              <button
                type="button"
                onClick={() => navigator.clipboard.writeText(text)}
                className="inline-flex h-8 items-center gap-1.5 rounded-full bg-[#ece8e1] px-3 font-body text-xs text-text-primary"
              >
                <Copy className="size-3.5" aria-hidden />
                Copy
              </button>
            ) : null}
          </div>
          <p className="mt-3 whitespace-pre-line font-body text-base leading-6 text-text-primary">
            {pending ? "Generating…" : (text ?? "No AI notes available.")}
          </p>
        </div>
      ) : null}
    </div>
  );
}
